import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import get from 'lodash/get';
import { Grid } from '../../../../CommonComponents/OPL/Grid/Grid';
import { Row } from '../../../../CommonComponents/OPL/Grid/Row';
import { Column } from '../../../../CommonComponents/OPL/Grid/Column';
import { Text } from '../../../../CommonComponents/OPL/Text/Text';
import { Select } from '../../../../CommonComponents/OPL/Select/Select/Select';
import { yesNoOpts } from '../../AccountOpening.meta';
import { MarginProps } from '../../../../theme/styledProps';
import { Input } from '../../../../CommonComponents/OPL/Input/Input';
import { Wrapper } from '../../../../CommonComponents/OPL/Wrapper/Wrapper';
import { InputMask } from '../../../../CommonComponents/OPL/Input/InputMask';
import { ABBREVIATED_STATES_US } from '../../../../CommonConstants';

const fieldName = 'interestedPartyInfo';

export const InterestedParty: React.FC<MarginProps> = props => {
  const formBag = useFormContext();
  const {
    formState: { errors },
  } = formBag;

  const hasInterestedPartyFieldName = `${fieldName}.hasInterestedParty`;
  const firstNameFieldName = `${fieldName}.legalFirstName`;
  const lastNameFieldName = `${fieldName}.legalLastName`;
  const addressFieldName = `${fieldName}.streetAddress`;
  const cityFieldName = `${fieldName}.city`;
  const stateFieldName = `${fieldName}.state`;
  const postalCodeFieldName = `${fieldName}.postalCode`;

  const hasInterestedParty = formBag.watch(hasInterestedPartyFieldName);

  return (
    <Wrapper {...props}>
      <Controller
        control={formBag.control}
        name={hasInterestedPartyFieldName}
        render={({ field }) => (
          <Select
            value={yesNoOpts.find(({ value }) => value === field.value)}
            label="Would you like to add an interested party?"
            onChange={opt => {
              if (opt) {
                formBag.setValue(hasInterestedPartyFieldName, opt.value);
              }
            }}
            options={yesNoOpts}
          />
        )}
      />

      {hasInterestedParty && (
        <Grid>
          <Row mt="8" mb="4">
            <Column width={12}>
              <Text variant="formLabel">Please provide the interested party information</Text>
            </Column>
          </Row>

          <Row>
            <Column width={12}>
              <Text variant="formLabel" mb="4">
                Legal Name
              </Text>
            </Column>
          </Row>

          <Row>
            <Column width={6}>
              <Input
                {...formBag.register(firstNameFieldName)}
                type="text"
                placeholder="First name"
                formVariant={get(errors, firstNameFieldName) ? 'error' : undefined}
                helperText={get(errors, firstNameFieldName)?.message}
                aria-label="First name"
                autoComplete="given-name"
                maxLength={256}
              />
            </Column>

            <Column width={6}>
              <Input
                {...formBag.register(lastNameFieldName)}
                type="text"
                placeholder="Last name"
                formVariant={get(errors, lastNameFieldName) ? 'error' : undefined}
                helperText={get(errors, lastNameFieldName)?.message}
                aria-label="Last name"
                autoComplete="family-name"
                maxLength={256}
              />
            </Column>
          </Row>

          <Row>
            <Column width={12}>
              <Text variant="formLabel" mt="8" mb="4">
                Primary Address
              </Text>
            </Column>
          </Row>

          <Row>
            <Column width={12}>
              <Input
                {...formBag.register(addressFieldName)}
                type="text"
                placeholder="Street Name, #Apt, Floor"
                formVariant={get(errors, addressFieldName) ? 'error' : undefined}
                helperText={get(errors, addressFieldName)?.message}
                aria-label="Primary Address"
                autoComplete="address-line1"
                maxLength={256}
              />
            </Column>
          </Row>

          <Row mt="8">
            <Column width={6}>
              <Input
                {...formBag.register(cityFieldName)}
                type="text"
                placeholder="City"
                formVariant={get(errors, cityFieldName) ? 'error' : undefined}
                helperText={get(errors, cityFieldName)?.message}
                aria-label="City"
                autoComplete="address-level2"
                maxLength={256}
              />
            </Column>

            <Column width={3}>
              <Controller
                control={formBag.control}
                name={stateFieldName}
                render={({ field }) => (
                  <Select
                    innerRef={field.ref}
                    value={ABBREVIATED_STATES_US.find(({ value }) => value === field.value)}
                    onChange={opt => {
                      if (opt) {
                        formBag.setValue(stateFieldName, opt.value, { shouldValidate: true });
                      }
                    }}
                    options={ABBREVIATED_STATES_US}
                    placeholder="State"
                    formVariant={get(errors, stateFieldName) ? 'error' : undefined}
                    helperText={get(errors, stateFieldName)?.message}
                    aria-label="State"
                  />
                )}
              />
            </Column>

            <Column width={3}>
              <InputMask
                {...formBag.register(postalCodeFieldName)}
                type="tex"
                mask="99999"
                placeholder="Zip code"
                formVariant={get(errors, postalCodeFieldName) ? 'error' : undefined}
                helperText={get(errors, postalCodeFieldName)?.message}
                autoComplete="postal-code"
                aria-label="Zip code"
              />
            </Column>
          </Row>
        </Grid>
      )}
    </Wrapper>
  );
};
